import React from 'react';
import { Label } from "flowbite-react";

/**
 * Props for the NumberInput component
 * @property id - Unique identifier for the input
 * @property name - Form field name
 * @property label - Optional label text above input
 * @property value - Current input value
 * @property defaultValue - Default value for uncontrolled input
 * @property placeholder - Placeholder text
 * @property onChange - Callback function when input changes
 * @property min - Minimum allowed value (defaults to 0)
 * @property max - Maximum allowed value
 * @property className - Additional CSS classes
 * @property required - Whether the field is required
 * @property showAsterisk - Whether to show the required asterisk
 * @property disabled - Whether the input is disabled
 */
interface NumberInputProps {
  id: string;
  name: string;
  label?: string;
  value?: number;
  defaultValue?: number;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  className?: string;
  required?: boolean;
  showAsterisk?: boolean;
  disabled?: boolean;
}

/**
 * NumberInput Component
 * 
 * A reusable number input component that provides:
 * - Number input with min/max validation
 * - Label with optional required indicator
 * - Dark mode support
 * - Customizable styles
 * - Disabled state support
 * - Placeholder text
 */
const NumberInput: React.FC<NumberInputProps> = ({
  id,
  name,
  label,
  value,
  defaultValue,
  placeholder,
  onChange,
  min = 0,
  max,
  className = '',
  required = false,
  showAsterisk = false,
  disabled = false,
}) => {
  /**
   * Base input styles including:
   * - Full width
   * - Border and rounded corners
   * - Focus states
   * - Dark mode variants
   * - Proper text colors for both modes
   */
  const baseInputStyles = `
    block w-full p-2.5 rounded-lg
    border border-gray-300 
    focus:ring-blue-500 focus:border-blue-500 
    bg-gray-50 
    text-gray-900
    placeholder-gray-500
    dark:bg-slate-800 
    dark:border-gray-700 
    dark:placeholder-gray-400 
    dark:text-white
  `;

  /**
   * Renders the label with optional required indicator
   * Shows asterisk only when both required and showAsterisk are true
   */
  const renderLabel = () => {
    if (!label) return null;
    return (
      <Label htmlFor={id} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        {label}
        {showAsterisk && required && '*'}
      </Label>
    );
  };

  return (
    <div className="mb-4">
      {renderLabel()}
      <div className="mt-2">
        <input
          type="number"
          id={id}
          name={name}
          onChange={onChange}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          min={min}
          max={max}
          className={`${baseInputStyles} ${className}`}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default NumberInput; 